/**
 * Centralized API error handling utility
 *
 * Provides consistent error handling across the application
 * Makes it easy for junior developers to handle API errors
 */

import { ApiError } from '../services/api/core'
import { useToast } from '../components/Toast'
import { logger } from './logger'
import { ERROR_MESSAGES, CHAT_ERROR_MESSAGES } from '../constants/errorMessages'

/**
 * Handle API error and return user-friendly message
 *
 * @param error - The error object (can be ApiError, Error, or unknown)
 * @param defaultMessage - Default message if error type is unknown
 * @param resource - Optional resource name for permission errors (e.g., "model", "prompt")
 * @returns User-friendly error message
 *
 * @example
 * ```tsx
 * try {
 *   await api.createProject(data);
 * } catch (err) {
 *   const message = handleApiError(err, 'Failed to create project');
 *   toast.error(message);
 * }
 * ```
 */
function readErrorCodeFromRecord(record: Record<string, unknown>): string {
  if (typeof record.error_code === 'string' && record.error_code) return record.error_code
  if (typeof record.code === 'string' && record.code) return record.code
  return ''
}

function getApiErrorCode(error: ApiError): string {
  if (error.code) return error.code
  const data = error.data
  if (!data || typeof data !== 'object') return ''
  const root = data as Record<string, unknown>
  const fromRoot = readErrorCodeFromRecord(root)
  if (fromRoot) return fromRoot
  const detail = root.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const fromDetail = readErrorCodeFromRecord(detail as Record<string, unknown>)
    if (fromDetail) return fromDetail
  }
  const nested = root.error
  if (nested && typeof nested === 'object') {
    return readErrorCodeFromRecord(nested as Record<string, unknown>)
  }
  return ''
}

/** Structured fields for frontend logger / telemetry (no raw response bodies). */
export function apiErrorToLogContext(error: unknown): Record<string, unknown> {
  if (error instanceof ApiError) {
    return {
      httpStatus: error.status,
      errorCode: getApiErrorCode(error),
      requestId: error.requestId || undefined,
    }
  }
  return {}
}

/**
 * Map structured connector error codes (from backend ValidationError / ConnectorError)
 * to user-friendly messages. Returns null when no mapping applies.
 */
export function getConnectorErrorMessage(error: ApiError): string | null {
  const code = getApiErrorCode(error).toUpperCase()
  const msg = error.message.toLowerCase()

  // Structured codes from backend ValidationError
  switch (code) {
    case 'AUTH_IDENTITY_MISMATCH':
      return ERROR_MESSAGES.CONNECTOR_AUTH_MISMATCH
    case 'AUTH_MODE_REQUIRED':
      return ERROR_MESSAGES.CONNECTOR_AUTH_MODE_REQUIRED
    case 'MISSING_REQUIRED_FIELD':
      if (msg.includes('password')) return ERROR_MESSAGES.CONNECTOR_PASSWORD_REQUIRED
      return error.message
    case 'DATABASE_NOT_FOUND':
      return ERROR_MESSAGES.CONNECTOR_DB_NOT_FOUND
    case 'RELATION_NOT_FOUND':
      return ERROR_MESSAGES.CONNECTOR_RELATION_NOT_FOUND
    case 'RESOURCE_NOT_FOUND':
      if (msg.includes('catalog')) return ERROR_MESSAGES.CONNECTOR_CATALOG_NOT_FOUND
      return ERROR_MESSAGES.CONNECTOR_DB_NOT_FOUND
    case 'SERVICE_UNAVAILABLE':
      return ERROR_MESSAGES.CONNECTOR_BUSY
  }

  // For CONNECTOR_ERROR (502) and generic connection errors, inspect message
  if (code === 'CONNECTOR_ERROR' || error.status === 502) {
    if (msg.includes('timed out') || msg.includes('timeout')) return ERROR_MESSAGES.CONNECTOR_TIMEOUT
    if (msg.includes('no route to host') || msg.includes('error 113')) return ERROR_MESSAGES.CONNECTOR_NO_ROUTE
    if (msg.includes('relation') && msg.includes('not found')) return ERROR_MESSAGES.CONNECTOR_RELATION_NOT_FOUND
    if (msg.includes('relation') && msg.includes('does not exist')) return ERROR_MESSAGES.CONNECTOR_RELATION_NOT_FOUND
    if (msg.includes('does not exist') || msg.includes('database not found')) return ERROR_MESSAGES.CONNECTOR_DB_NOT_FOUND
    if (msg.includes('auth_oid') || msg.includes('auth identity')) return ERROR_MESSAGES.CONNECTOR_AUTH_MISMATCH
    if (msg.includes('all connection attempts failed')) return ERROR_MESSAGES.CONNECTOR_NO_ROUTE
  }

  return null
}

/**
 * Returns true for connector errors that are deterministic and should not be auto-retried.
 * (auth mismatch, missing credential, resource not found, etc.)
 */
export function isNonRetryableConnectorError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = getApiErrorCode(error).toUpperCase()
  const nonRetryable = new Set([
    'AUTH_IDENTITY_MISMATCH',
    'AUTH_MODE_REQUIRED',
    'MISSING_REQUIRED_FIELD',
    'DATABASE_NOT_FOUND',
    'RESOURCE_NOT_FOUND',
    'RELATION_NOT_FOUND',
  ])
  if (nonRetryable.has(code)) return true

  // Check message-based patterns for CONNECTOR_ERROR
  const msg = error.message.toLowerCase()
  if (msg.includes('relation') && (msg.includes('not found') || msg.includes('does not exist'))) return true
  if (msg.includes('auth_oid') || msg.includes('auth identity mismatch')) return true
  if (msg.includes('does not exist') && msg.includes('database')) return true
  return false
}

export function isRelationNotFoundError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = getApiErrorCode(error).toUpperCase()
  if (code === 'RELATION_NOT_FOUND') return true
  const msg = error.message.toLowerCase()
  return msg.includes('relation') && (msg.includes('not found') || msg.includes('does not exist'))
}

export function isCrossTenantDataPreviewError(error: unknown): boolean {
  return error instanceof ApiError && getApiErrorCode(error).toUpperCase() === 'CROSS_TENANT_DATA_PREVIEW'
}

/**
 * Feature-grant denials name the RBAC action ("delete agent-workflows").
 * That verb is the missing grant, not proof the caller tried to delete a record.
 */
const FEATURE_GRANT_DENIAL_RE =
  /(?:don't|do not) have permission to (?:view|read|create|edit|update|modify|delete|remove|write|unmask)\b/i

function isFeatureGrantDenial(message: string): boolean {
  return FEATURE_GRANT_DENIAL_RE.test(message)
}

const LEGACY_PENDING_DECISION = 'only pending requests can be decided'
const CURRENT_STATUS_RE = /current=([a-z_]+)/i

/**
 * Map stale registry-request decide errors (legacy backend string or new copy)
 * to user-facing text. Returns null when the message is unrelated.
 */
export function friendlyRegistryRequestAlreadyDecidedMessage(raw: string): string | null {
  const lower = raw.toLowerCase()
  if (lower.includes(LEGACY_PENDING_DECISION)) {
    const match = CURRENT_STATUS_RE.exec(raw)
    const status = match?.[1]?.toLowerCase()
    if (status === 'approved') return ERROR_MESSAGES.REQUEST_ALREADY_APPROVED
    if (status === 'rejected') return ERROR_MESSAGES.REQUEST_ALREADY_REJECTED
    return ERROR_MESSAGES.REQUEST_NO_LONGER_PENDING
  }
  if (lower.includes('this request was already approved')) {
    return ERROR_MESSAGES.REQUEST_ALREADY_APPROVED
  }
  if (lower.includes('this request was already rejected')) {
    return ERROR_MESSAGES.REQUEST_ALREADY_REJECTED
  }
  if (lower.includes('this request is no longer pending')) {
    return ERROR_MESSAGES.REQUEST_NO_LONGER_PENDING
  }
  return null
}

export function isRegistryRequestAlreadyDecidedError(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (getApiErrorCode(error).toUpperCase() === 'REQUEST_ALREADY_DECIDED') return true
    return friendlyRegistryRequestAlreadyDecidedMessage(error.message) !== null
  }
  if (error instanceof Error) {
    return friendlyRegistryRequestAlreadyDecidedMessage(error.message) !== null
  }
  if (typeof error === 'string') {
    return friendlyRegistryRequestAlreadyDecidedMessage(error) !== null
  }
  return false
}

/**
 * True for knowledge registration conflicts, including the legacy rollup that
 * nested `409: {'error': "RegisteredTable ... already exists on this connection"}`.
 * Scoped so unrelated "already exists" conflicts are left unchanged.
 */
export function isKnowledgeAlreadyRegisteredMessage(message: string): boolean {
  const lower = message.toLowerCase()
  if (lower.includes('this knowledge is already registered')) return true
  if (lower.includes('already registered for this connector')) return true
  if (lower.includes('already registered under your account')) return true
  const nestedTableConflict =
    lower.includes('already exists on this connection') &&
    (lower.includes('registeredtable') || lower.startsWith('failed to register tables'))
  if (nestedTableConflict) return true
  return (
    lower.startsWith('failed to register tables:') && lower.includes('is already registered')
  )
}

function registryRequestAlreadyDecidedFromApiError(error: ApiError): string | null {
  const fromMessage = friendlyRegistryRequestAlreadyDecidedMessage(error.message)
  if (fromMessage) return fromMessage
  if (getApiErrorCode(error).toUpperCase() === 'REQUEST_ALREADY_DECIDED') {
    return ERROR_MESSAGES.REQUEST_NO_LONGER_PENDING
  }
  return null
}

export function handleApiError(
  error: unknown,
  defaultMessage: string = ERROR_MESSAGES.GENERIC,
  resource?: string
): string {
  if (error instanceof ApiError) {
    // Handle 401 Unauthorized errors
    if (error.status === 401) {
      // Check if it's a token expiration or general auth failure
      const errorMessage = error.message.toLowerCase()
      if (errorMessage.includes('expired') || errorMessage.includes('token')) {
        return ERROR_MESSAGES.TOKEN_EXPIRED
      }
      if (errorMessage.includes('login') || errorMessage.includes('authenticate')) {
        return ERROR_MESSAGES.AUTHENTICATION_REQUIRED
      }
      return ERROR_MESSAGES.AUTHENTICATION_FAILED
    }

    // Handle 403 Forbidden errors specifically for RBAC
    if (error.status === 403) {
      if (isCrossTenantDataPreviewError(error)) {
        return ERROR_MESSAGES.CROSS_TENANT_DATA_PREVIEW
      }
      const msg = error.message || ''
      const errorMessage = msg.toLowerCase()
      // Backend often returns full, actionable copy (export/import/RBAC). Don't replace with generic templates.
      if (
        errorMessage.includes('contact your administrator') ||
        errorMessage.includes('request access') ||
        errorMessage.includes('skills export') ||
        errorMessage.includes('skills import') ||
        (errorMessage.includes('skills') && errorMessage.includes("don't have permission")) ||
        isFeatureGrantDenial(msg)
      ) {
        return msg || ERROR_MESSAGES.FORBIDDEN_ERROR
      }

      // Check if error message contains action type
      if (errorMessage.includes('update') || errorMessage.includes('modify')) {
        return resource
          ? ERROR_MESSAGES.PERMISSION_DENIED_UPDATE(resource)
          : ERROR_MESSAGES.PERMISSION_DENIED
      }

      if (errorMessage.includes('delete') || errorMessage.includes('remove')) {
        return resource
          ? ERROR_MESSAGES.PERMISSION_DENIED_DELETE(resource)
          : ERROR_MESSAGES.PERMISSION_DENIED
      }

      if (
        errorMessage.includes('view') ||
        errorMessage.includes('access') ||
        errorMessage.includes('see')
      ) {
        return resource
          ? ERROR_MESSAGES.PERMISSION_DENIED_VIEW(resource)
          : ERROR_MESSAGES.PERMISSION_DENIED
      }

      if (errorMessage.includes('create')) {
        return resource
          ? ERROR_MESSAGES.PERMISSION_DENIED_CREATE(resource)
          : ERROR_MESSAGES.PERMISSION_DENIED
      }

      // Generic 403 error
      return error.message || ERROR_MESSAGES.FORBIDDEN_ERROR
    }

    // Bulk duplicate knowledge registration, including the legacy nested 409 dump.
    if (error.status === 400 || error.status === 409) {
      const code = getApiErrorCode(error).toUpperCase()
      if (code === 'TABLES_ALREADY_REGISTERED' || isKnowledgeAlreadyRegisteredMessage(error.message)) {
        return ERROR_MESSAGES.TABLES_ALREADY_REGISTERED
      }
    }

    if (error.status === 413) {
      return error.message || ERROR_MESSAGES.PAYLOAD_TOO_LARGE
    }

    const alreadyDecided = registryRequestAlreadyDecidedFromApiError(error)
    if (alreadyDecided) return alreadyDecided

    // Connector-specific structured error codes
    const connectorMsg = getConnectorErrorMessage(error)
    if (connectorMsg) return connectorMsg

    // For other ApiErrors, use the message from backend
    return error.message
  }

  if (error instanceof Error) {
    const decided = friendlyRegistryRequestAlreadyDecidedMessage(error.message)
    if (decided) return decided
    // Log unexpected errors for debugging
    logger.error('Unexpected error', error)
    return error.message || defaultMessage
  }

  // Handle string errors
  if (typeof error === 'string') {
    return friendlyRegistryRequestAlreadyDecidedMessage(error) ?? error
  }

  // Unknown error type
  logger.error('Unknown error type', error)
  return defaultMessage
}

/**
 * Handle API error with toast notification
 *
 * Convenience function that handles error and shows toast
 *
 * @param error - The error object
 * @param toast - Toast context from useToast()
 * @param defaultMessage - Default message if error type is unknown
 * @param resource - Optional resource name for permission errors
 * @returns User-friendly error message
 *
 * @example
 * ```tsx
 * const toast = useToast();
 *
 * try {
 *   await api.createProject(data);
 * } catch (err) {
 *   handleApiErrorWithToast(err, toast, 'Failed to create project');
 * }
 * ```
 */
export function handleApiErrorWithToast(
  error: unknown,
  toast: ReturnType<typeof useToast>,
  defaultMessage: string = ERROR_MESSAGES.GENERIC,
  resource?: string
): string {
  const message = handleApiError(error, defaultMessage, resource)
  // If the error is a 401 Unauthorized, do NOT display a raw toast notification.
  // The global AuthContext is already redirecting the user and will show a clean toast/modal.
  if (error instanceof ApiError && error.status === 401) {
    return message
  }
  toast.error(message)
  return message
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthenticationError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401
  }
  return false
}

/**
 * Check if error is a permission denied error (403)
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 403
  }
  return false
}

/**
 * Permission-denied check for callers that do NOT go through `ApiError`.
 *
 * The CWB and ABA service clients throw a plain `Error` carrying the status in
 * its message, so `isPermissionDeniedError` alone misses them and a 403 reads
 * as a generic failure. Use this wherever the error may come from one of those
 * clients; prefer `isPermissionDeniedError` when the call uses `request()`.
 */
export function isForbiddenLikeError(error: unknown): boolean {
  if (isPermissionDeniedError(error)) return true
  if (error && typeof error === 'object' && 'status' in error) {
    if ((error as { status?: number }).status === 403) return true
  }
  const message = error instanceof Error ? error.message : String(error ?? '')
  return /\b403\b/.test(message)
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 404
  }
  return false
}

function errorMessageText(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return ''
}

/** Agentserve 404 when session_id + agent_id + tenant_id do not match a row. */
export function isSessionNotFoundError(error: unknown): boolean {
  const message = errorMessageText(error).toLowerCase()
  if (message.includes('session not found')) return true
  if (!(error instanceof ApiError)) return false
  const code = getApiErrorCode(error).toUpperCase()
  if (code === 'SESSION_NOT_FOUND') return true
  const data = error.data
  if (data && typeof data === 'object') {
    const detail = (data as Record<string, unknown>).detail
    if (typeof detail === 'string' && detail.toLowerCase().includes('session not found')) {
      return true
    }
  }
  return false
}

/** Chat send/create after the agent was hard-deleted (410) or archived (403 AGENT_ARCHIVED). */
export function isAgentUnavailableForChat(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = getApiErrorCode(error).toUpperCase()
  if (code === 'AGENT_UNAVAILABLE' || error.status === 410) return true
  return code === 'AGENT_ARCHIVED'
}

export function getAgentChatGoneMessage(error: unknown): string {
  if (error instanceof ApiError && getApiErrorCode(error).toUpperCase() === 'AGENT_ARCHIVED') {
    return CHAT_ERROR_MESSAGES.AGENT_ARCHIVED.message
  }
  return CHAT_ERROR_MESSAGES[410].message
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 0 || error.status >= 500
  }
  if (error instanceof Error) {
    return (
      error.message.includes('network') ||
      error.message.includes('fetch') ||
      error.message.includes('timeout')
    )
  }
  return false
}

/**
 * Check if error is an expected client-side error that can be downgraded in telemetry
 */
export function isClientError(error: unknown): boolean {
  if (error instanceof ApiError) {
    const apiError = error as ApiError
    if (apiError.status === 401 || apiError.status === 403 || apiError.status === 404) {
      return true
    }
    return isExpectedUpstreamClientMiss(apiError)
  }
  return isExpectedUpstreamClientMiss(error)
}

/** Upstream 4xx / missing-deployment mapped to our 502 — not our outage. */
const UPSTREAM_CLIENT_MISS_RE =
  /http\s*4\d\d|status code:\s*4\d\d|invalid request|deployment not found|model not found|resource not found/i

/**
 * True when a 502 (or raw 4xx) is the other side rejecting the call.
 * Reachability / genuine infra 502s stay false so telemetry can still ERROR.
 */
function collectUpstreamMissMessageParts(error: object): string[] {
  const parts: string[] = []
  if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    parts.push((error as { message: string }).message)
  }
  const data = 'data' in error ? (error as { data?: unknown }).data : undefined
  if (!data || typeof data !== 'object') return parts
  const record = data as Record<string, unknown>
  if (typeof record.message === 'string') parts.push(record.message)
  if (typeof record.error === 'string') parts.push(record.error)
  const nested = record.error
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>
    if (typeof inner.message === 'string') parts.push(inner.message)
  }
  return parts
}

export function isExpectedUpstreamClientMiss(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false
  }
  const status = 'status' in error ? (error as { status?: number }).status : undefined
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return true
  }
  if (status !== 502) {
    return false
  }
  return UPSTREAM_CLIENT_MISS_RE.test(collectUpstreamMissMessageParts(error).join(' '))
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (error instanceof ApiError) {
    const apiError = error as ApiError
    return apiError.status >= 500
  }
  return false
}

/**
 * Get retry suggestion based on error type
 */
export function getRetrySuggestion(error: unknown): string | null {
  if (isNetworkError(error)) {
    return 'Please check your internet connection and try again'
  }
  if (isServerError(error)) {
    return 'Server error. Please try again in a few moments'
  }
  return null
}

/**
 * Chat error message result with structured data for display in chat
 */
export interface ChatErrorMessage {
  title: string
  message: string
  action?: string
  statusCode?: number
  fullMessage: string // Combined message for display
}

/**
 * True when the error string is a raw HTTP dump / JSON envelope that should
 * not be shown verbatim in chat UI.
 */
function errorText(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return ''
}

const NAMED_RESOURCE_NOT_FOUND =
  /\b(Model|Tool|Prompt|Skill|Connection|Storage|Knowledge) resource \S+ not found\b/i

/**
 * Sentence that already names a missing platform resource, e.g. `Model resource gpt-5.1 not found`.
 */
export function namedResourceNotFoundSentence(message: string): string | null {
  const match = NAMED_RESOURCE_NOT_FOUND.exec(message || '')
  if (!match?.[1]) return null
  const type = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase()
  const tail = match[0].split(/ resource /i)[1]
  if (!tail) return null
  const ident = tail.replace(/ not found$/i, '').trim()
  if (!ident) return null
  return `${type} resource ${ident} not found`
}

/**
 * True when the backend reported a graph visit-limit stop or an unmapped `__end__` route.
 */
export function isGraphVisitLimitMessage(message: string): boolean {
  const trimmed = message.trim()
  const lower = trimmed.toLowerCase()
  if (
    lower.includes('maximum node visits') ||
    lower.includes('maximum graph visit') ||
    lower.includes('max_graph_visits') ||
    lower.includes('graph_visit_limit')
  ) {
    return true
  }
  if (lower.includes('__end__')) return true
  if (/keyerror/.test(lower) && /end/.test(lower)) return true
  return /^['"`]end['"`]$/.test(trimmed)
}

/** Routing tokens must not be parsed as Markdown (`__end__` renders as bold "end"). */
export function isRoutingTokenErrorText(message: string): boolean {
  const trimmed = message.trim()
  if (/keyerror/i.test(trimmed)) return true
  if (/__end__/.test(trimmed)) return true
  return /^['"`]?end['"`]?$/.test(trimmed)
}

export function withGraphVisitNode(body: string, rawError: string, nodeId: unknown): string {
  if (typeof nodeId !== 'string' || !nodeId.trim() || !isGraphVisitLimitMessage(rawError)) {
    return body
  }
  return `${body} Stopped at ${nodeId.trim()}.`
}

/** Pull provider `message` from LangGraph node SSE errors (Python dict literals in the string). */
export function extractSseNodeErrorMessage(raw: string): string {
  const trimmed = raw?.trim()
  if (!trimmed) return raw

  const pythonStyle = trimmed.match(/'message':\s*'(.+?)',\s*'type'/)
  if (pythonStyle?.[1]) return pythonStyle[1]

  const jsonStyle = trimmed.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/)
  if (jsonStyle?.[1]) return jsonStyle[1].replace(/\\"/g, '"')

  return raw
}

/** Opaque Azure/APIM 404 blob: `Error code: 404 - {'statusCode': 404, 'message': 'Resource not found'}`. */
function isOpaqueProviderResourceNotFound(message: string): boolean {
  const lower = message.toLowerCase()
  if (!lower.includes('resource not found')) return false
  return (
    lower.includes('error code: 404') ||
    /statuscode['"]?\s*:\s*404/.test(lower) ||
    /['"]statuscode['"]\s*:\s*404/.test(lower) ||
    /status\s*code:\s*404/.test(lower)
  )
}

function isUnusableRawErrorMessage(message: string): boolean {
  const trimmed = message.trim()
  if (!trimmed) return true
  if (/^HTTP error!\s*status:\s*\d+/i.test(trimmed)) return true
  if (trimmed.startsWith('{') && /"error(_code)?"/.test(trimmed)) return true
  if (/status:\s*\d+\s*-\s*\{/.test(trimmed)) return true
  // Never prefer the raw APIM Resource-not-found blob over curated model-config copy.
  if (isOpaqueProviderResourceNotFound(trimmed)) return true
  return false
}

function isModelNotFoundMessage(message: string): boolean {
  const lower = message.toLowerCase()
  // Vision/OCR canvas setup errors are actionable config hints, not deleted models.
  if (/vision/.test(lower) && /ocr/.test(lower)) {
    return false
  }
  if (lower.includes('vision model not configured')) {
    return false
  }
  return (
    lower.includes('model not found') ||
    /model with id .+ not found/.test(lower) ||
    (lower.includes('model') && lower.includes('not found')) ||
    /model\s+(is\s+)?not configured/.test(lower) ||
    lower.includes('model deployment or endpoint was not found') ||
    lower.includes("check the node's model configuration") ||
    isOpaqueProviderResourceNotFound(message)
  )
}

/**
 * Get a user-friendly error message for chat interfaces.
 * This returns a structured error message that can be displayed
 * directly in the chat history instead of a toast notification.
 *
 * @param error - The error object (can be ApiError, Error, or unknown)
 * @param context - Optional context for the error (e.g., 'left model', 'right model')
 * @returns ChatErrorMessage with title, message, and optional action
 *
 * @example
 * ```tsx
 * try {
 *   await sendMessage(content);
 * } catch (err) {
 *   const errorMsg = getChatErrorMessage(err);
 *   setMessages(prev => [...prev, {
 *     role: 'assistant',
 *     content: '',
 *     error: errorMsg.fullMessage,
 *   }]);
 * }
 * ```
 */
export function getChatErrorMessage(error: unknown, context?: string): ChatErrorMessage {
  if (isGraphVisitLimitMessage(errorText(error))) {
    const info = CHAT_ERROR_MESSAGES.graph_visit_limit
    let fullMessage = info.message
    if (context) {
      fullMessage = `[${context}] ${fullMessage}`
    }
    return {
      title: info.title,
      message: info.message,
      action: info.action,
      fullMessage,
    }
  }

  const namedResource = namedResourceNotFoundSentence(errorText(error))
  if (namedResource) {
    const resourceType = namedResource.split(' ')[0] || 'Resource'
    let fullMessage = namedResource
    if (context) {
      fullMessage = `[${context}] ${fullMessage}`
    }
    return {
      title: `${resourceType} not found`,
      message: namedResource,
      action: 'Check that this resource is still in the provider subscription, then try again.',
      statusCode: 404,
      fullMessage,
    }
  }

  if (isSessionNotFoundError(error)) {
    const info = CHAT_ERROR_MESSAGES.session_not_found
    const statusCode = error instanceof ApiError ? error.status : 404
    let fullMessage = info.message
    if (context) {
      fullMessage = `[${context}] ${fullMessage}`
    }
    return {
      title: info.title,
      message: info.message,
      action: info.action,
      statusCode,
      fullMessage,
    }
  }

  let statusCode: number | undefined
  let errorInfo = CHAT_ERROR_MESSAGES.unknown
  let errorCode = ''

  // Extract status code from ApiError
  if (error instanceof ApiError) {
    statusCode = error.status
    errorCode = (error.code || '').toLowerCase()
    const structuredCode = getApiErrorCode(error).toUpperCase()

    // Check for specific status code message
    if (statusCode && CHAT_ERROR_MESSAGES[statusCode]) {
      errorInfo = CHAT_ERROR_MESSAGES[statusCode]
    }
    if (structuredCode === 'AGENT_UNAVAILABLE' || statusCode === 410) {
      errorInfo = CHAT_ERROR_MESSAGES[410]
    } else if (structuredCode === 'AGENT_ARCHIVED') {
      errorInfo = CHAT_ERROR_MESSAGES.AGENT_ARCHIVED
    }

    // Handle specific error patterns from the error message
    const errorMessage = error.message.toLowerCase()

    // Rate limit detection (might not have 429 status in all cases)
    if (
      errorMessage.includes('rate limit') ||
      errorMessage.includes('rate-limited') ||
      errorMessage.includes('too many requests')
    ) {
      errorInfo = CHAT_ERROR_MESSAGES[429]
    }

    // Timeout detection
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      errorInfo = CHAT_ERROR_MESSAGES[408]
    }

    // Model not found / deleted
    if (
      statusCode === 404 ||
      errorCode === 'not_found' ||
      isModelNotFoundMessage(error.message)
    ) {
      errorInfo = CHAT_ERROR_MESSAGES[404]
    }

    // Multimodal attachment compatibility (backend ValidationError or provider 400)
    if (
      errorMessage.includes('does not support video attachment') ||
      errorMessage.includes('does not accept native video')
    ) {
      return {
        title: 'Unsupported attachment',
        message: 'This model does not support video attachments.',
        action: 'Choose a video-capable model or remove the video.',
        fullMessage:
          'This model does not support video attachments. Choose a video-capable model or remove the video.',
        statusCode: statusCode ?? 400,
      }
    }
    if (
      errorMessage.includes('does not support audio attachment') ||
      errorMessage.includes('does not accept native audio')
    ) {
      return {
        title: 'Unsupported attachment',
        message: 'This model does not support audio attachments.',
        action: 'Choose an audio-capable model or remove the audio.',
        fullMessage:
          'This model does not support audio attachments. Choose an audio-capable model or remove the audio.',
        statusCode: statusCode ?? 400,
      }
    }

    // Project membership / agent chat access (before generic API-key patterns)
    if (
      errorMessage.includes('must be a member') ||
      errorMessage.includes('project to chat') ||
      errorMessage.includes('chat with this agent') ||
      errorMessage.includes('request access from the agent owner') ||
      errorCode === 'forbidden'
    ) {
      const detail =
        error instanceof ApiError && error.message
          ? error.message
          : "You don't have permission to chat with this agent."
      return {
        title: 'Access Denied',
        message: detail,
        action:
          'Ask a project admin to add you as a member, or request Agent Store access for UAT/PROD.',
        fullMessage: detail,
        statusCode: statusCode ?? 403,
      }
    }

    if (
      errorMessage.includes('tagp azure auth') ||
      errorMessage.includes('azure_ad_tenant_id') ||
      errorMessage.includes('azure ad environment')
    ) {
      const detail =
        error instanceof ApiError && error.message
          ? error.message
          : 'TAGP Azure Auth requires platform Azure AD environment variables.'
      return {
        title: 'Azure authentication not configured',
        message: detail,
        action:
          'Set AZURE_AD_TENANT_ID, AZURE_AD_CLIENT_ID, and AZURE_AD_CLIENT_SECRET, or edit the agent to use IAM or direct credentials.',
        fullMessage: detail,
        statusCode: statusCode ?? 400,
      }
    }

    // API key issues
    if (
      errorMessage.includes('api key') ||
      errorMessage.includes('authentication') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('credentials')
    ) {
      errorInfo = {
        title: 'Authentication Error',
        message: 'There was an issue authenticating with the model provider.',
        action: 'Please check your API key configuration.',
      }
    }
  } else if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase()
    const statusFromError =
      typeof (error as Error & { status?: number }).status === 'number'
        ? (error as Error & { status?: number }).status
        : undefined
    if (statusFromError) {
      statusCode = statusFromError
      if (CHAT_ERROR_MESSAGES[statusFromError]) {
        errorInfo = CHAT_ERROR_MESSAGES[statusFromError]
      }
    }

    if (
      errorMessage.includes('does not support video attachment') ||
      errorMessage.includes('does not accept native video')
    ) {
      return {
        title: 'Unsupported attachment',
        message: 'This model does not support video attachments.',
        action: 'Choose a video-capable model or remove the video.',
        fullMessage:
          'This model does not support video attachments. Choose a video-capable model or remove the video.',
        statusCode: 400,
      }
    }
    if (
      errorMessage.includes('does not support audio attachment') ||
      errorMessage.includes('does not accept native audio')
    ) {
      return {
        title: 'Unsupported attachment',
        message: 'This model does not support audio attachments.',
        action: 'Choose an audio-capable model or remove the audio.',
        fullMessage:
          'This model does not support audio attachments. Choose an audio-capable model or remove the audio.',
        statusCode: 400,
      }
    }

    // Model not found / not configured (e.g. from SSE stream when model was revoked)
    if (statusCode === 404 || isModelNotFoundMessage(error.message)) {
      errorInfo = CHAT_ERROR_MESSAGES[404]
      statusCode = statusCode ?? 404
    }

    // Network error detection
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('econnrefused') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('connection')
    ) {
      errorInfo = CHAT_ERROR_MESSAGES.network
    }

    if (error.name === 'AbortError') {
      errorInfo = {
        title: 'Request Cancelled',
        message: 'The request was cancelled.',
        action: 'You can send a new message when ready.',
      }
    }
  }

  let rawMessage: string | undefined
  if (error instanceof Error) {
    rawMessage = error.message
  } else if (typeof error === 'string') {
    rawMessage = error
  } else if (error && typeof error === 'object' && 'message' in error) {
    rawMessage = String((error as { message: unknown }).message)
  }

  // Prefer a clean API message over the generic fallback — but never surface
  // raw HTTP dumps / JSON envelopes (those belong in logs only).
  const isGenericMessage = rawMessage
    ? [
        CHAT_ERROR_MESSAGES.unknown.message,
        ERROR_MESSAGES.SOMETHING_WENT_WRONG,
        ERROR_MESSAGES.GENERIC,
        ERROR_MESSAGES.FORBIDDEN_ERROR,
        ERROR_MESSAGES.NETWORK_ERROR,
      ].includes(rawMessage)
    : true

  const mappedToStatusMessage =
    statusCode !== undefined && errorInfo === CHAT_ERROR_MESSAGES[statusCode]

  if (
    rawMessage &&
    !isGenericMessage &&
    !isUnusableRawErrorMessage(rawMessage) &&
    // Keep the curated 404 model-deleted copy instead of "Model with id '…' not found"
    !(mappedToStatusMessage && statusCode === 404)
  ) {
    errorInfo = {
      ...errorInfo,
      message: rawMessage,
    }

    const lowerRawMessage = rawMessage.toLowerCase()
    // Provide a better title/action for quota issues
    if (lowerRawMessage.includes('quota') || lowerRawMessage.includes('billing')) {
      errorInfo.title = 'Quota Exceeded'
      // Only override action if we don't have one or it's the generic one
      if (!errorInfo.action || errorInfo.action === CHAT_ERROR_MESSAGES.unknown.action) {
        errorInfo.action = 'Please check your API account status and billing details.'
      }
    } else if (errorInfo.action === CHAT_ERROR_MESSAGES.unknown.action) {
      // Clear the generic "Please try again." action if we are showing a custom message
      delete errorInfo.action
    }
  }

  // Body only — callers set metadata.errorTitle / errorAction separately.
  // Do not embed **Title** or action here (avoids duplicate headings/actions).
  let fullMessage = errorInfo.message
  if (context) {
    fullMessage = `[${context}] ${fullMessage}`
  }

  return {
    title: errorInfo.title,
    message: errorInfo.message,
    action: errorInfo.action,
    statusCode,
    fullMessage,
  }
}

/**
 * Determine if an error should show a retry button in chat
 * Returns true for transient errors that might succeed on retry
 */
export function isRetryableError(error: unknown): boolean {
  if (isGraphVisitLimitMessage(errorText(error))) {
    return false
  }
  if (isSessionNotFoundError(error)) {
    return true
  }
  if (error instanceof ApiError) {
    if (isAgentUnavailableForChat(error)) {
      return false
    }
    // 5xx errors are usually transient
    if (error.status >= 500 && error.status < 600) {
      return true
    }
    // Rate limits can be retried after waiting
    if (error.status === 429) {
      return true
    }
    // Timeouts can be retried
    if (error.status === 408 || error.status === 504) {
      return true
    }
  }

  // Network errors are usually transient
  if (isNetworkError(error)) {
    return true
  }

  return false
}

/**
 * Shape of error metadata persisted on assistant messages in the backend.
 * Used by `mapPersistedErrorMetadata` to hydrate UI error fields on load.
 */
export interface PersistedErrorMetadata {
  error?: string
  error_title?: string
  error_action?: string
  can_retry?: boolean
  status_code?: number | null
  original_text?: string
}

/**
 * Map persisted backend error metadata (snake_case) to the camelCase fields
 * that `ChatMessageComponent` and studio chat surfaces consume.
 *
 * Returns `null` when the metadata does not contain an error.
 */
export function mapPersistedErrorMetadata(
  metadata: Record<string, unknown> | null | undefined,
  messageMetadata?: Record<string, unknown> | null | undefined
): {
  error: string
  errorTitle: string | undefined
  errorAction: string | undefined
  canRetry: boolean
  statusCode: number | undefined
  originalText: string | undefined
} | null {
  const md = metadata ?? messageMetadata
  if (!md || !md.error) return null

  const errorStr = String(md.error)
  const errorTitle = md.error_title ? String(md.error_title) : undefined
  const errorAction = md.error_action ? String(md.error_action) : undefined
  const canRetry = typeof md.can_retry === 'boolean' ? md.can_retry : true
  const statusCode =
    typeof md.status_code === 'number' ? md.status_code : undefined
  const originalText = md.original_text ? String(md.original_text) : undefined

  return { error: errorStr, errorTitle, errorAction, canRetry, statusCode, originalText }
}
